import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Sparkles, ArrowRight, MapPin } from "lucide-react";
import SiteLayout from "@/components/SiteLayout";
import StarField from "@/components/StarField";
import { memorialsApi } from "@/lib/api";
import { hexFor, STAR_COLOR_MAP } from "@/lib/starColors";

export default function Sky() {
    const navigate = useNavigate();
    const [memorials, setMemorials] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterColor, setFilterColor] = useState(null);

    useEffect(() => {
        memorialsApi
            .list()
            .then((d) => setMemorials(d.memorials || []))
            .finally(() => setLoading(false));
    }, []);

    // Count per color (only colors that actually have at least one memorial)
    const colorCounts = useMemo(() => {
        const counts = {};
        for (const m of memorials) {
            const k = m.star_color || "_unset";
            counts[k] = (counts[k] || 0) + 1;
        }
        return counts;
    }, [memorials]);

    const visible = useMemo(() => {
        if (!filterColor) return memorials;
        return memorials.filter((m) => (m.star_color || "_unset") === filterColor);
    }, [memorials, filterColor]);

    const starPoints = visible.map((m, i) => ({
        id: m.id || i,
        x: m.star_x ?? Math.random(),
        // Spread vertically from 8% to 92% — much more room as the sky grows
        y: 0.08 + (m.star_y ?? Math.random()) * 0.84,
        title: m.name,
        colorHex: hexFor(m.star_color),
        onClick: () => navigate(`/taubenhimmel/${m.slug}`),
        testid: `sky-star-${m.slug}`,
    }));

    return (
        <SiteLayout>
            {/* SKY HEADER */}
            <section className="relative">
                <div className="absolute inset-0 night-sky" />
                <StarField density={140} className="absolute inset-0" />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-12">
                    <div className="label mb-4">Digitaler Taubenhimmel</div>
                    <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none max-w-3xl">
                        Ein leuchtender Stern{" "}
                        <span className="italic text-[#f0c674]">für jede Taube</span>, die uns
                        wichtig war.
                    </h1>
                    <p className="text-[#94a3b8] text-lg mt-6 max-w-2xl leading-relaxed">
                        Jeder Punkt am Himmel ist eine Geschichte. Klicke auf einen Stern oder
                        scrolle nach unten, um die Gedenkseiten zu besuchen.
                    </p>
                    <div className="mt-10 flex flex-col sm:flex-row gap-4">
                        <Link to="/erstellen" className="btn-primary" data-testid="sky-cta-create">
                            <Sparkles size={16} /> Eigene Taube eintragen
                        </Link>
                        <a href="#liste" className="btn-ghost" data-testid="sky-cta-list">
                            Alle Gedenkseiten ansehen
                        </a>
                        <button
                            type="button"
                            className="btn-ghost"
                            onClick={() => {
                                const slug = window.prompt(
                                    "Wie lautet der Slug deiner Gedenkseite? Du findest ihn am Ende der URL (z. B. luna-abc123)."
                                );
                                if (!slug) return;
                                const email = window.prompt("Mit welcher E-Mail hast du sie angelegt?");
                                if (!email) return;
                                memorialsApi.requestEditLink(slug.trim(), email.trim())
                                    .then(() => toast.success("Falls die Angaben passen, ist der Link unterwegs."))
                                    .catch(() => toast.error("Etwas ging schief. Bitte später erneut versuchen."));
                            }}
                            data-testid="sky-request-edit-link"
                        >
                            Bearbeitungs-Link verloren?
                        </button>
                    </div>
                </div>
            </section>

            {/* INTERACTIVE SKY MAP */}
            <section className="relative">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
                    <div className="relative h-[480px] sm:h-[640px] lg:h-[780px] glass overflow-hidden" data-testid="sky-canvas">
                        <StarField density={160} className="absolute inset-0" />
                        <StarField density={1} className="absolute inset-0" interactive points={starPoints} />
                        {memorials.length === 0 && !loading && (
                            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
                                <Sparkles size={32} className="text-[#f0c674] mb-4" strokeWidth={1.3} />
                                <p className="font-display text-2xl mb-3">Der Himmel wartet auf seine Sterne.</p>
                                <p className="text-[#94a3b8] max-w-md">
                                    Sei die erste Person, die ihrer Taube hier einen Platz schenkt.
                                </p>
                                <Link to="/erstellen" className="btn-primary mt-6" data-testid="sky-empty-cta">
                                    Eintrag erstellen
                                </Link>
                            </div>
                        )}
                    </div>

                    {/* COLOR LEGEND / FILTER */}
                    {memorials.length > 0 && (
                        <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="sky-color-legend">
                            <button
                                type="button"
                                className={`chip ${!filterColor ? "active" : ""}`}
                                onClick={() => setFilterColor(null)}
                                data-testid="sky-filter-all"
                            >
                                Alle <span className="font-mono text-[10px] ml-1 opacity-70">{memorials.length}</span>
                            </button>
                            {Object.entries(STAR_COLOR_MAP).map(([key, meta]) => {
                                const count = colorCounts[key] || 0;
                                if (count === 0) return null;
                                const active = filterColor === key;
                                return (
                                    <button
                                        type="button"
                                        key={key}
                                        className={`chip ${active ? "active" : ""}`}
                                        onClick={() => setFilterColor(active ? null : key)}
                                        data-testid={`sky-filter-${key}`}
                                    >
                                        <span
                                            className="w-2.5 h-2.5 rounded-full"
                                            style={{ background: meta.hex, boxShadow: `0 0 8px ${meta.hex}80` }}
                                        />
                                        {meta.label}
                                        <span className="font-mono text-[10px] ml-1 opacity-70">{count}</span>
                                    </button>
                                );
                            })}
                            {(colorCounts._unset || 0) > 0 && (
                                <button
                                    type="button"
                                    className={`chip ${filterColor === "_unset" ? "active" : ""}`}
                                    onClick={() => setFilterColor(filterColor === "_unset" ? null : "_unset")}
                                    data-testid="sky-filter-unset"
                                >
                                    Ohne Farbwahl
                                    <span className="font-mono text-[10px] ml-1 opacity-70">{colorCounts._unset}</span>
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </section>

            {/* MEMORIAL LIST */}
            <section id="liste" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-24">
                <div className="flex items-end justify-between mb-10">
                    <div>
                        <div className="label mb-3">{filterColor ? "Gefiltert" : "Alle Gedenkseiten"}</div>
                        <h2 className="font-display text-3xl sm:text-4xl">
                            {visible.length}{" "}
                            {visible.length === 1 ? "Taube" : "Tauben"} im Himmel
                            {filterColor && filterColor !== "_unset" && (
                                <span className="ml-3 text-base text-[#94a3b8] font-mono">
                                    in {STAR_COLOR_MAP[filterColor]?.label || filterColor}
                                </span>
                            )}
                        </h2>
                    </div>
                    {filterColor && (
                        <button
                            type="button"
                            onClick={() => setFilterColor(null)}
                            className="text-[#94a3b8] hover:text-[#f0c674] text-sm"
                            data-testid="sky-filter-clear"
                        >
                            Filter aufheben ✕
                        </button>
                    )}
                </div>

                {loading ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {Array.from({ length: 6 }).map((_, i) => (
                            <div key={i} className="glass aspect-[4/5] animate-pulse" />
                        ))}
                    </div>
                ) : visible.length === 0 ? (
                    <div className="glass p-12 text-center">
                        <p className="text-[#94a3b8]">
                            {filterColor ? "Keine Tauben in dieser Farbe." : "Noch keine Einträge."}
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {visible.map((m, i) => (
                            <MemorialCard key={m.id || m.slug} m={m} index={i} />
                        ))}
                    </div>
                )}
            </section>
        </SiteLayout>
    );
}

function MemorialCard({ m, index }) {
    const BACKEND = process.env.REACT_APP_BACKEND_URL;
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: Math.min(index * 0.05, 0.4) }}
        >
            <Link
                to={`/taubenhimmel/${m.slug}`}
                className="glass gold-glow group block overflow-hidden transition-all duration-500 hover:-translate-y-1"
                data-testid={`memorial-card-${m.slug}`}
            >
                <div className="aspect-[4/5] overflow-hidden relative">
                    {m.has_image ? (
                        <MemorialImage slug={m.slug} alt={m.name} />
                    ) : (
                        <div className="w-full h-full bg-gradient-to-br from-[#0c1222] to-[#1a2541] flex items-center justify-center">
                            <Sparkles size={32} className="text-[#f0c674]/40" />
                        </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#040710] via-transparent to-transparent opacity-70" />
                </div>
                <div className="p-6">
                    <h3 className="font-display text-2xl mb-1">{m.name}</h3>
                    {m.location && (
                        <div className="flex items-center gap-1.5 text-xs text-[#94a3b8] mb-3">
                            <MapPin size={12} /> {m.location}
                        </div>
                    )}
                    <p className="text-sm text-[#94a3b8] line-clamp-2 leading-relaxed">
                        {m.story || m.message || "—"}
                    </p>
                    <div className="mt-4 flex items-center justify-between text-xs text-[#64748b]">
                        <span>{m.candles ?? 0} Kerzen · {m.feathers ?? 0} Federn</span>
                        <span className="inline-flex items-center gap-1 text-[#f0c674]">
                            Besuchen <ArrowRight size={12} />
                        </span>
                    </div>
                </div>
            </Link>
        </motion.div>
    );
}

function MemorialImage({ slug, alt }) {
    const [src, setSrc] = useState(null);
    useEffect(() => {
        let cancelled = false;
        memorialsApi
            .image(slug)
            .then((d) => {
                if (!cancelled) setSrc(d.data);
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [slug]);
    if (!src) return <div className="w-full h-full bg-[#0c1222] animate-pulse" />;
    return <img src={src} alt={alt} className="w-full h-full object-cover" />;
}
